import React, { useEffect, useState } from "react";
import styles from './Evaluations.module.scss';
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { useToasts } from "../../../contexts/ToastContext";
import axiosClient from "../../../api/axiosClient";
import LoadingOverlay from "../../../components/loadingOverlay/LoadingOverlay";
import PageTitle from "../../../components/pageTitle/PageTitle";
import StudentAllocationCard from "../../../components/studentAllocationCard/StudentAllocationCard";
import LoadMore from "../../../components/loadMore/loadMore";
import { format } from "date-fns";
import Search from "../../../components/search/Search";
import { Tab, TabPanel, Tabs } from "../../../components/tabs/Tabs";
import Dropdown from "../../../components/dropdown/Dropdown";

const Evalauations = () => {
  const { role, can } = useAuth();
  const isDirector = role !== 'Program Coordinator';
  const canConfirmEvaluations = isDirector || can('canConfirmEvaluations');
  const limit = 20;
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadMoreLoading, setIsLoadMoreLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [studentsPage, setStudentsPage] = useState(1);
  const [studentsTotalPages, setStudentsTotalPages] = useState(1);
  const [isStudentsLoading, setIsStudentsLoading] = useState(false);
  const [isStudentsLoadMoreLoading, setIsStudentsLoadMoreLoading] = useState(false);
  const [evaluations, setEvaluations] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [yearList, setYearList] = useState([{ value: null, label: 'All' }]);
  const [specialityList, setSpecialityList] = useState([{ value: null, label: 'All' }]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedSpeciality, setSelectedSpeciality] = useState(null);
  const navigate = useNavigate();
  const { showToast } = useToasts();

  useEffect(() => {
    const fetchData = async () => {
      await axiosClient.get(`years/details`)
      .then((response) => {
        const years = response.data.map((item) => ({
          value: item.year.id,
          label: item.year.year,
        }));
        setYearList([{ value: null, label: 'All' }, ...years]);
      })
      .catch((error) => { showToast(error.message, 'error'); });

      await axiosClient.get(`specialities`)
      .then((response) => {
        const specialities = response.data.map((item) => ({
          value: item.id,
          label: item.name,
        }));
        setSpecialityList([{ value: null, label: 'All' }, ...specialities]);
      });
    };

    fetchData();
  },[showToast]);

  useEffect(() => {
    const fetchStudents = async () => {
      if(studentsPage === 1) {
        setIsLoading(true);
        setIsStudentsLoading(true);
      } else {
        setIsStudentsLoadMoreLoading(true);
      }

      const params = {
        limit: limit,
        page: studentsPage,
        search: search,
      };

      if (selectedYear) {
        params.yearId = selectedYear;
      }

      if (selectedSpeciality) {
        params.specialityIds = [selectedSpeciality];
      }

      await axiosClient.get(`users/student-management/students`, {
        params: params
      })
      .then ((response) => {
        if(response.data?.students) {
          if(studentsPage === 1) {
            setStudents([]);
          }
          setStudents((prevData) => {
            return [...prevData, ...response.data.students.map((student) => ({
              id: student.id,
              profilePicture: student?.profilePicture ?? null,
              firstName: student?.firstName ?? '',
              lastName: student?.lastName ?? '',
              data: [
                {
                  label: 'Instructor',
                  value: `${student?.allocation?.instructor?.firstName ?? ''} ${student?.allocation?.instructor?.lastName ?? ''}`
                },
                {
                  label: 'Speciality',
                  value: student?.specialities.length > 0 ? 
                    `${student?.specialities[0].name} ${student?.specialities.length > 1 
                      ? `+${student?.specialities.length - 1}` : ''}` : 
                    '',
                  tooltip: student?.specialities.length > 1 ? 
                    student?.specialities.map(item => item.name).join(', ') :
                    ''
                },
                {
                  label: 'Semester',
                  value: student?.semester?.semester ?? ''
                },
                {
                  label: 'Year',
                  value: student?.year?.year ?? ''
                },
                {
                  label: 'Start Date',
                  value: student?.allocation?.startDate ? format(student?.allocation?.startDate, "MM/dd/yyyy") : ''
                },
                {
                  label: 'End Date',
                  value: student?.allocation?.endDate ? format(student?.allocation?.endDate, "MM/dd/yyyy") : ''
                },
                {
                  label: 'Graduation Date',
                  value: student?.rotationEndDate ? format(student?.rotationEndDate, "MM/dd/yyyy") : ''
                },
              ],
            }))];
          });
          setStudentsTotalPages(response.data.totalPages);
        }
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsLoading(false);
        setIsStudentsLoading(false);
        setIsStudentsLoadMoreLoading(false);
      });
    };

    fetchStudents();
  }, [studentsPage, search, showToast, selectedYear, selectedSpeciality]);

  useEffect(() => {
    setStudentsPage(1);
    setStudents([]);
  }, [selectedYear, selectedSpeciality, search]);

  const loadMoreStudents = async () => {
    setStudentsPage(studentsPage + 1);
  };

  useEffect(() => {
    const fetchData = async () => {
      if(page === 1) {
        setIsLoading(true);
      } else {
        setIsLoadMoreLoading(true);
      }
      await axiosClient.get(`evaluations/by-students`, {
        params: {
          limit: limit,
          page: page,
          search: search,
        }
      })
      .then ((response) => {
        if(response.data?.evaluations) {
          if(page === 1) {
            setEvaluations([]);
          }
          setEvaluations((prevData) => {
            return [...prevData, ...response.data.evaluations.map((evaluation) => ({
              id: evaluation.id,
              profilePicture: evaluation?.student?.profilePicture ?? null,
              firstName: evaluation?.student?.firstName ?? '',
              lastName: evaluation?.student?.lastName ?? '',
              data: [
                {
                  label: 'Evaluation Id',
                  value: evaluation?.id
                },
                {
                  label: 'Status',
                  value: evaluation?.approvalStatus
                },
                {
                  label: 'Instructor',
                  value: `${evaluation?.instructor?.firstName} ${evaluation?.instructor?.lastName}`
                },
                {
                  label: 'Year',
                  value: evaluation?.student?.year?.year ?? ''
                },
                {
                  label: 'Semester',
                  value: evaluation?.student?.semester?.semester ?? ''
                },
                {
                  label: 'Month',
                  value: evaluation?.student?.month?.month ?? ''
                },
                {
                  label: 'Date',
                  value: evaluation?.date ? format(evaluation?.date, 'MM/dd/yyyy') : ''
                },
              ],
            }))];
          });
          setTotalPages(response.data.totalPages);
        }
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsLoading(false);
        setIsLoadMoreLoading(false);
      });
    };

    fetchData();
  }, [page, search, showToast]);

  const loadMore = async () => {
    setPage(page + 1);
  };

  const handleSearch = (value) => {
    setSearch(value);
    setPage(1);
    setStudentsPage(1);
  };

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.header}>
        <PageTitle
          label="Evaluations"
        />
        <div className={styles.searchWrapper}>
          <Search 
            onSubmit={handleSearch}
          />
        </div>
      </div>
      <Tabs defaultExpanded>
        {isDirector && <Tab><span>All Evaluations</span></Tab>}
        {canConfirmEvaluations && <Tab><span>Confirm Evaluation</span></Tab>}

        {isDirector && <TabPanel>
          <div className={styles.container}>
            <div className={styles.filters}>
              <div className={styles.dropdown}>
                <Dropdown
                  label="Year"
                  options={yearList}
                  value={selectedYear}
                  onChange={(value) => setSelectedYear(value)}
                />
              </div>
              <div className={styles.specialityDropdown}>
                <Dropdown
                  label="Speciality"
                  options={specialityList}
                  value={selectedSpeciality}
                  onChange={(value) => setSelectedSpeciality(value)}
                />
              </div>
            </div>
            { students.length > 0 ? <div className={styles.cardList}>
              {students.map((student, index) => {
                return (
                  <div className={styles.cardItem} key={`student-${index}`}>
                    <StudentAllocationCard
                      {...student}
                      onClick={() => navigate(`/evaluations/student/${student.id}`)}
                    />
                  </div>
                );
              })}
              {(isStudentsLoadMoreLoading || studentsPage < studentsTotalPages) 
                && <LoadMore isLoading={isStudentsLoadMoreLoading} onClick={loadMoreStudents} />}
            </div> : !isStudentsLoading && <div className={styles.emptyEvaluations}>
              <p>You have no students at this time.</p>
            </div>}
          </div>
        </TabPanel>}

        {canConfirmEvaluations && <TabPanel>
          <div className={styles.container}>
            { evaluations.length > 0 ? <div className={styles.cardList}>
              {evaluations.map((evaluation, index) => {
                return (
                  <div className={styles.cardItem} key={`student-${index}`}>
                    <StudentAllocationCard
                      {...evaluation}
                      onClick={() => navigate(`/evaluations/${evaluation.id}/summary`)}
                    />
                  </div>
                );
              })}
              {(isLoadMoreLoading || page < totalPages) 
                && <LoadMore isLoading={isLoadMoreLoading} onClick={loadMore} />}
            </div> : !isLoading && <div className={styles.emptyEvaluations}>
              <p>You have no evaluations at this time.</p>
            </div>}
          </div>
        </TabPanel>}
      </Tabs>
    </>
  );
};

export default Evalauations;